import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { colors } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { ModalContent, ModalFooter } from '@creditornot/cb-modal';
import React from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  registerOrderApiWebhook,
  updateOrderApiWebhook,
} from 'apis/restaurant-api/merchant-admin/order-api-credentials/api';
import { NullableOrderApiWebhookResponse } from 'apis/restaurant-api/merchant-admin/order-api-credentials/types';
import { RFFFieldErrorWrapper } from 'common/components';
import validateYupSchema from 'common/utils/validateYupSchema';

import { createWebhookFormSchema } from './utils';

type OrderApiWebhookFormProps = {
  merchantId: string;
  credentialId: string;
  existingWebhook: NullableOrderApiWebhookResponse;
  onClose: () => void;
  onSubmit: () => void;
};

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
`;

const ModalButton = styled(Button)`
  margin: 0 8px;
`;

type FormData = {
  webhookUrl: string;
  clientSecret: string;
};

const ErrorMessage = styled(Alert).attrs({ variant: 'error' })`
  background-color: white;
  color: ${colors.strawberry100};
  padding: 8px 0 0;
`;

const SetUpOrderApiWebhookForm = ({
  existingWebhook,
  onClose,
  onSubmit,
  credentialId,
  merchantId,
}: OrderApiWebhookFormProps) => {
  const intl = useIntl();

  const initialData = {
    clientSecret: '',
    webhookUrl: existingWebhook ? existingWebhook.url : '',
  };
  const formSchema = createWebhookFormSchema(intl);

  const _onSubmit = async (data: FormData) => {
    try {
      if (existingWebhook) {
        await updateOrderApiWebhook(merchantId, credentialId, existingWebhook.id, {
          webhook_url: data.webhookUrl,
          client_secret: data.clientSecret,
        });
      } else {
        await registerOrderApiWebhook(merchantId, credentialId, {
          webhook_url: data.webhookUrl,
          client_secret: data.clientSecret,
        });
      }

      onSubmit();
      return {};
    } catch (err) {
      if (err.name === 'ValidationError' && err.type) {
        return { webhookUrl: err.errors[0] };
      }
      return { webhookUrl: intl.formatMessage({ id: 'errors.unknown-error-retry-contact' }) };
    }
  };

  return (
    <Form<FormData>
      initialValues={initialData}
      onSubmit={_onSubmit}
      validate={validateYupSchema(formSchema)}
      render={({ handleSubmit, pristine, hasValidationErrors, submitting, dirtySinceLastSubmit, submitErrors }) => (
        <>
          <form onSubmit={handleSubmit}>
            <ModalContent>
              <FormRow
                required
                horizontalPadding
                labelHtmlFor="webhookUrl"
                label={intl.formatMessage({ id: 'merchant-integrations.order-api.webhook.url' })}
              >
                <Field name="webhookUrl">
                  {field => (
                    <RFFFieldErrorWrapper field={field}>
                      {input => <Input {...input} id="webhook-url" autoComplete="off" />}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              </FormRow>
              <FormRow
                required
                horizontalPadding
                labelHtmlFor="clientSecret"
                label={intl.formatMessage({ id: 'merchant-integrations.order-api.webhook.client-secret' })}
              >
                <Field name="clientSecret">
                  {field => (
                    <RFFFieldErrorWrapper field={field}>
                      {input => <Input {...input} id="client-secret" autoComplete="off" />}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              </FormRow>
              {!dirtySinceLastSubmit && submitErrors && <ErrorMessage>{submitErrors.webhookUrl}</ErrorMessage>}
            </ModalContent>
            <StyledModalFooter>
              <ModalButton
                variant="lightBlue"
                size="small"
                onClick={onClose}
                disabled={submitting}
                loading={submitting}
              >
                <FormattedMessage id="state.cancel" />
              </ModalButton>
              <ModalButton
                variant="blue"
                size="small"
                type="submit"
                disabled={pristine || hasValidationErrors || submitting}
                loading={submitting}
              >
                <FormattedMessage id="merchant-integrations.order-api.webhook.save" />
              </ModalButton>
            </StyledModalFooter>
          </form>
        </>
      )}
    />
  );
};

export default SetUpOrderApiWebhookForm;
